import React, { useState } from 'react';
import { useFeatureVisibility } from './hooks/useFeatureVisibility';
import { useUsageReportingData } from './hooks/useUsageReportingData';
import { getDefaultTimeframe } from './utils/getDefaultTimeframe';
import TimeframeFilter from '../../components/timeframeFilter/TimeframeFilter';
import {
  Tabset,
  TabList,
  NavTab,
  TabContent,
  TabPanel,
} from '../../components/tabs/Tabs';
import TripsMonitoredTab from './TripsMonitoredTab';
import TripStatusApiTab from './TripStatusApiTab';
import type { TimeframeSelection } from '../../types/timeframe';
import styles from './usageReporting.module.css';
import moment from 'moment';

type ActiveTab = 'monitored' | 'statusApi';

/** Format timeframe into readable subtitle like "past year, grouped by month" */
const formatTimeframeSubtitle = (timeframe: TimeframeSelection): string => {
  const start = moment(timeframe.startDate);
  const end = moment(timeframe.endDate);
  const startFormatted = start.format('MMM D, YYYY');
  const endFormatted = end.format('MMM D, YYYY');
  return `${startFormatted} - ${endFormatted}, grouped by ${timeframe.granularity === 'monthly' ? 'month' : timeframe.granularity === 'weekly' ? 'week' : 'day'}`;
};

/** Format just the date range, e.g. "Jun 1, 2026 - Jun 30, 2026" (shown in scorecards). */
const formatDateRange = (timeframe: TimeframeSelection): string => {
  const start = moment(timeframe.startDate);
  const end = moment(timeframe.endDate);
  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
};

const UsageReportingContainer: React.FC = () => {
  const features = useFeatureVisibility();
  const [timeframe, setTimeframe] = useState<TimeframeSelection>(getDefaultTimeframe());
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const { tripsMonitoredData, tripStatusData, loading, tripsMonitoredError, tripStatusError } = useUsageReportingData(timeframe, fetchTrigger);
  const [activeTab, setActiveTab] = useState<ActiveTab>('monitored');

  const handleTimeframeChange = (newTimeframe: TimeframeSelection) => {
    setTimeframe(newTimeframe);
    setFetchTrigger(prev => prev + 1);
    setActiveTab('monitored');
  };

  const timeframeSubtitle = formatTimeframeSubtitle(timeframe);
  const dateRangeLabel = formatDateRange(timeframe);

  return (
    <div className={styles.container}>
      <div className={styles.pageTitle}>TRIP USAGE DASHBOARD</div>
      <TimeframeFilter value={timeframe} onChange={handleTimeframeChange} />
      <Tabset>
        <TabList>
          <NavTab
            name="Trips Monitored"
            onSelect={() => setActiveTab('monitored')}
            active={activeTab === 'monitored'}
          />
          <NavTab
            name="Trip Status API"
            onSelect={() => setActiveTab('statusApi')}
            active={activeTab === 'statusApi'}
          />
        </TabList>
        <TabContent>
          <TabPanel active={activeTab === 'monitored'}>
            <TripsMonitoredTab
              data={tripsMonitoredData}
              loading={loading}
              error={tripsMonitoredError}
              showAlertsSent={features.showAlertsSent}
              showEmailsSent={features.showEmailsSent}
              showWaiverMetric={features.showWaiverMetric}
              timeframeSubtitle={timeframeSubtitle}
              dateRangeLabel={dateRangeLabel}
              granularity={timeframe.granularity}
            />
          </TabPanel>
          <TabPanel active={activeTab === 'statusApi'}>
            <TripStatusApiTab
              data={tripStatusData}
              loading={loading}
              error={tripStatusError}
              timeframeSubtitle={timeframeSubtitle}
              dateRangeLabel={dateRangeLabel}
              granularity={timeframe.granularity}
            />
          </TabPanel>
        </TabContent>
      </Tabset>
    </div>
  );
};

export default UsageReportingContainer;
